/* 베타테스터 자리 배정 (#beta 신청 페이지 전용).

   미리 발급해 둔 학원코드를 beta_slots/{1..N}에 한 자리씩 심어 두고, 신청자가 한 자리씩 선점한다.
   보안이 여기 걸려 있다: 학원코드는 워커에서 사실상 출입증이므로 남은 코드 목록이 브라우저에
   노출되면 안 된다. 그래서 Firestore 규칙이 (1) uid가 비어 있는 자리만 update 허용 → 선점이
   곧 배타적 잠금, (2) uid가 본인인 자리만 read 허용 → 선점 전에는 코드를 볼 수 없게 막는다.
   즉 아래 update 성공/실패는 규칙이 내리는 판정이며, 클라이언트 로직으로 우회되지 않는다.

   신청자는 익명 로그인(팝업 없음)으로 uid만 발급받는다. 이메일 소유는 확인하지 못하므로
   자리 주인은 이메일이 아니라 uid로 판정한다 — 이메일은 나중에 그 구글 계정으로 앱에 로그인할
   때 codes/{이메일} 조회에 쓰이는 값일 뿐이다. */

// 실제로 심어둔 자리 수와 맞춰야 한다 (secrets/beta-pool.mjs status로 확인). 작으면 뒷자리가 안 쓰인다.
export const POOL_SIZE = 125;

const isMinorSchool = (s) => s === "중학생" || s === "고등학생";
export const SCHOOLS = ["졸업자", "N수생", "대학생·대학원생", "고등학생", "중학생", "기타"];
export const needsGuardian = isMinorSchool;
// "기타"는 나이를 유추할 수 없다 — 본인이 미성년이라고 밝힐 때만 보호자 동의를 받는다.
export const ASKS_AGE = "기타";

const slotRef = (db, slot) => db.collection("beta_slots").doc(String(slot));
// 규칙이 이름을 40자로 끊는다 — 넘기면 이름만 잘리는 게 아니라 쓰기 자체가 거부된다.
const trimName = (s) => String(s || "").trim().slice(0, 40);

export async function claimSlot(db, email, uid, { size = POOL_SIZE, now = Date.now, rand = Math.random } = {}) {
  // 재신청 먼저 확인한다. 규칙이 "내 uid가 적힌 자리"만 읽게 해주므로, 읽히는 자리가 곧 내 자리다.
  // 빈 자리도 남의 자리도 여기서는 전부 거부되어 null로 떨어진다.
  const seen = await Promise.all(Array.from({ length: size }, (_, i) =>
    slotRef(db, i + 1).get().then((s) => (s && s.exists ? s.data() : null), () => null)));
  const at = seen.findIndex((d) => d && d.uid === uid);
  if (at >= 0) return { code: seen[at].code, slot: at + 1, reused: true };

  /* 무작위 위치에서 시작해 한 바퀴 돈다. 앞에서부터 훑으면 찬 자리마다 거부 왕복이 그대로 쌓여
     풀이 찰수록 뒤에 온 사람이 느려진다(자리 200개가 다 차기 직전이면 왕복 200번). 시작을 흩어
     놓으면 시도 횟수가 자리 수가 아니라 "찬 비율"에만 매인다. 대가로 자리 번호는 순서대로 안 나간다. */
  const from = Math.floor(rand() * size);
  for (let i = 0; i < size; i++) {
    const slot = ((from + i) % size) + 1;
    const ref = slotRef(db, slot);
    try { await ref.update({ email, uid, ts: now() }); }
    catch (e) { continue; }               // 규칙이 거부 = 이미 남이 찜한 자리
    const snap = await ref.get();         // 내 자리가 된 뒤에야 코드를 읽을 수 있다
    const code = snap && snap.exists ? (snap.data() || {}).code : "";
    if (code) return { code, slot, reused: false };
  }
  throw new Error("베타 테스터 자리가 모두 찼어요");
}

/* 공개 가입: 신청서 없이 구글 로그인만 한 사람도 자리를 하나 잡아 자기 코드를 받는다.
   전원이 코드 하나를 나눠 쓰면 워커 사용량이 그 코드로 뭉쳐 사람별 비용 집계도, 한 사람만 끊는 것도
   불가능해진다 — 그래서 신청자와 같은 풀에서 한 자리씩 가져간다(대신 풀이 닳는다).
   app_config/open_beta는 값이 아니라 존재 여부만 본다 = 그 문서를 지우는 것이 유입 차단 스위치다.
   자리가 다 차면 claimSlot이 던지고, 부르는 쪽(platform.js)이 받아 개인 모드로 떨군다.
   codes 문서는 규칙상 create만 되므로 이미 있으면 쓰기는 조용히 실패한다 — 코드는 자리에서 오니 그대로다. */
export async function joinOpenBeta(db, email, uid, opts = {}) {
  const snap = await db.collection("app_config").doc("open_beta").get().catch(() => null);
  if (!snap || !snap.exists) return "";
  const { code, slot } = await claimSlot(db, email, uid, opts);
  // adult은 남기지 않는다 — 나이를 묻지 않았으므로 false로 적으면 "미성년"이라는 틀린 사실이 된다.
  // 실패해도 코드는 돌려준다(자리는 이미 내 것). 다만 조용히 넘기면 명부만 비는 상태를 아무도 모른다 —
  // 규칙 거부로 실제로 겪었다. 다음 로그인 때 이 set이 다시 돌아 스스로 메운다.
  // 이름은 구글 계정 표시이름 — 신청서를 안 받았으니 이게 유일한 단서다.
  await db.collection("codes").doc(email).set({ code, slot, name: trimName(opts.name) })
    .catch((e) => console.warn("[beta] 명부 기록 실패 — 코드는 배정됨:", (e && e.message) || e));
  return code;
}

/* 이름 뒤늦게 채우기: 이름 없이 명부에 올라간 사람(구글 로그인만으로 자리를 받은 경로)을
   다음 로그인 때 표시이름으로 한 번 메운다. 이미 이름이 있거나 표시이름이 없으면 아무것도 안 한다 —
   규칙도 같은 조건이라 여기서 거르지 않으면 매 로그인마다 거부되는 쓰기를 한 번씩 날리게 된다. */
export async function backfillName(db, email, current, name) {
  const n = trimName(name);
  if (current || !n) return false;
  await db.collection("codes").doc(email).update({ name: n });
  return true;
}

/* 신청 접수: 연락처(beta_apply)와 이메일→코드 매핑(codes)을 남긴다.
   둘 다 규칙이 create만 허용하므로, 같은 이메일로 다른 브라우저에서 다시 신청하면(= uid가 달라
   재신청으로 못 알아본다) 여기서 거부된다. 자리를 하나 소모한 뒤 실패하는 드문 경로라 감수하고,
   대신 사용자에게는 "이미 신청됨"으로 알려준다. */
export async function registerBeta(db, info, opts = {}) {
  const email = String(info.email || "").trim().toLowerCase();
  const { code, slot, reused } = await claimSlot(db, email, String(info.uid || ""), opts);
  if (reused) return { code, slot, reused };

  const adult = info.adult !== undefined ? !!info.adult : !isMinorSchool(info.school);
  const ts = (opts.now || Date.now)();
  try {
    await db.collection("beta_apply").doc(email).set({ name: info.name || "", phone: info.phone || "",
      school: info.school || "", adult, guardian: !!info.guardian, slot, ts });
    await db.collection("codes").doc(email).set({ code, slot, name: info.name || "", adult });
  } catch (e) {
    throw new Error("이미 신청된 이메일이에요. 다른 이메일로 신청하거나 문의해주세요.");
  }
  return { code, slot, reused };
}
